import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  clubService,
  liveMarketService,
  userService,
} from "../../services/api"; // Corrected import path
import { ROLES } from "../../config/roles";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import ReportDataIssueButton from "../../components/common/ReportDataIssueButton";
import ClubMatchesTab from "../../components/club-matches/ClubMatchesTab";
import NationalityFlags from "../../components/common/NationalityFlags";
import toast from "react-hot-toast";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation, getStaticTemplateMatch } from "../../translations/registerStaticTranslations";
import { formatCurrencyWithSymbol } from "../../utils/currencyFormatter";

const ClubProfile = () => {
  const { clubId } = useParams();
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — look the sentence up the same way the scanner does (exact
  // match first, template match second, e.g. for the "{count}" share count).
  const currentLanguage = useUiLanguage();
  const tr = (s) =>
    getStaticTranslation(i18n, s, currentLanguage) ||
    getStaticTemplateMatch(i18n, s, currentLanguage) ||
    s;
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("squad");
  const [activePosition, setActivePosition] = useState("all");
  const [squadFilter, setSquadFilter] = useState("all"); // all, available_loan, available_sale
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const { currentRole } = useSelector((state) => state.auth);
  const [showShareModal, setShowShareModal] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const navigate = useNavigate();
  const [messaging, setMessaging] = useState(false);
  useEffect(() => {
    const fetchClubData = async () => {
      if (!clubId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await clubService.getClubPublicProfile(clubId);
        if (response.success && response.data) {
          setClub(response.data);
          console.log("Fetched Club Data:", response.data);
        } else {
          throw new Error(
            response.error || "Failed to fetch club data or data is missing"
          );
        }
      } catch (err) {
        console.error("Failed to fetch club data:", err);
        setError(
          err.message || "Failed to load club profile. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchClubData();
  }, [clubId]);

  // Fetch current user profile to decide Share button visibility
  useEffect(() => {
    const fetchProfile = async () => {
      const res = await userService.getProfile();
      if (res.success) setCurrentUserProfile(res.data);
    };
    fetchProfile();
  }, []);

  // Fetch staff data when staff tab is active
  useEffect(() => {
    const fetchStaffData = async () => {
      if (activeTab !== "staff") return;

      setStaffLoading(true);
      setStaffError(null);
      try {
        const response = await clubService.getClubStaff(clubId);
        if (response.success) {
          setStaff(response.data || []);
        } else {
          setStaffError(response.error || "Failed to fetch staff data");
        }
      } catch (err) {
        console.error("Failed to fetch staff data:", err);
        setStaffError("Failed to load staff data. Please try again later.");
      } finally {
        setStaffLoading(false);
      }
    };

    fetchStaffData();
  }, [activeTab, clubId]);

  const tabs = [
    { id: "squad", label: "Players" },
    { id: "staff", label: "Staff" },
    { id: "trophies", label: "Trophies" },
    { id: "matches", label: "Matches" },
  ];

  const positionTabs = [
    { id: "all", label: "All positions" },
    { id: "goalkeeper", label: "Goalkeeper" },
    { id: "defender", label: "Defender" },
    { id: "midfielder", label: "Midfielder" },
    { id: "forward", label: "Forward" },
  ];

  // Component to display Staff
  const StaffSection = ({ staffData, loading, error }) => {
    const { ref: scrollRef, isDragging, handlers } = useDragScroll();

    if (loading) {
      return (
        <div className="flex items-center justify-center py-8 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center text-red-500 py-8 bg-white rounded-lg border border-red-200 shadow-sm">
          <p>{error}</p>
        </div>
      );
    }

    if (!staffData || staffData.length === 0) {
      return (
        <div className="text-center text-gray-500 py-8 bg-white rounded-lg border border-gray-200 shadow-sm">
          No staff found for this club.
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden border border-[#D6DBEA]">
        <div
          ref={scrollRef}
          {...handlers}
          className="overflow-x-auto"
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          }}
        >
          <table
            className="min-w-full divide-y divide-gray-200 table-sticky-first"
            style={{ minWidth: "1200px" }}
          >
            <thead className="bg-[#F7F7F7]">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Staff Type
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Role Title
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Nationality
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Languages
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Contract Expiry
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  LinkedIn
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Website
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {staffData.map((member) => (
                <tr
                  key={member.id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
                        {member.name
                          ? member.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                          : "N/A"}
                      </div>
                      <div>
                        <h4 className="font-bold text-[16px] text-[#222A35]">
                          {member.name || "N/A"}
                        </h4>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35] capitalize">
                    {member.staff_type
                      ? member.staff_type.replace(/_/g, " ")
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.role_title || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.nationality || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.languages && member.languages.length > 0
                      ? member.languages.join(", ")
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.contract_expiry_date || "N/A"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.linkedin_url ? (
                      <a
                        href={member.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        LinkedIn
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {member.website_url ? (
                      <a
                        href={member.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Website
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Share support
  const handleShare = async (selectedUsers, shareUrl) => {
    try {
      console.log("Sharing with users:", selectedUsers);
      console.log("Share URL:", shareUrl);
      alert(tr(`Shared club profile with ${selectedUsers.length} user(s)!`));
    } catch (error) {
      console.error("Error sharing:", error);
      alert(tr("Failed to share. Please try again."));
    }
  };

  const shareUrl = `${window.location.origin}/search/club/${clubId}`;

  // Hook for drag-to-scroll functionality
  const useDragScroll = () => {
    const ref = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleMouseDown = (e) => {
      if (!ref.current) return;
      setIsDragging(true);
      setStartX(e.pageX - ref.current.offsetLeft);
      setScrollLeft(ref.current.scrollLeft);
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !ref.current) return;
      e.preventDefault();
      const x = e.pageX - ref.current.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      ref.current.scrollLeft = scrollLeft - walk;
    };

    return {
      ref,
      isDragging,
      handlers: {
        onMouseDown: handleMouseDown,
        onMouseLeave: handleMouseLeave,
        onMouseUp: handleMouseUp,
        onMouseMove: handleMouseMove,
      },
    };
  };

  // Component to display Squad
  const SquadSection = ({ squadData }) => {
    const { ref: scrollRef, isDragging, handlers } = useDragScroll();
    if (!squadData || squadData.length === 0) {
      return (
        <div className="text-center text-gray-500 py-8 bg-white rounded-lg border border-gray-200 shadow-sm">
          No squad data available.
        </div>
      );
    }

    const squadArray = Array.isArray(squadData) ? squadData : [];

    // Filter squad data based on availability status and position for TransferPitch users
    const filteredSquad = squadArray.filter((player) => {
      // First filter by availability
      let availabilityMatch = true;
      if (squadFilter === "available_loan")
        availabilityMatch = player.is_available_for_loan;
      if (squadFilter === "available_sale")
        availabilityMatch = player.is_available_for_sale;

      // Then filter by position
      let positionMatch = true;
      if (activePosition !== "all") {
        const normalizedPosition = (player.position || "").toLowerCase();
        const normalizedFilter = activePosition.replace("-", " ");
        positionMatch = normalizedPosition.includes(normalizedFilter);
      }

      return availabilityMatch && positionMatch;
    });

  const priorityOrder = ["goalkeeper", "defender", "midfielder", "forward"];
    const getPriority = (position) => {
      const normalized = (typeof position === "string" ? position : "").toLowerCase();
      const index = priorityOrder.findIndex((key) => normalized.includes(key));
      return index === -1 ? priorityOrder.length : index;
    };

    const sortedSquad = [...filteredSquad].sort((a, b) => {
      const priorityDiff = getPriority(a.position) - getPriority(b.position);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }

      // Within a position group: most valuable player first; players
      // without a market value go to the end of their group. Name stays
      // as the tie-breaker so equal/missing values keep a stable order.
      const mvA = typeof a.market_value === "number" ? a.market_value : null;
      const mvB = typeof b.market_value === "number" ? b.market_value : null;
      if (mvA !== mvB) {
        if (mvA === null) return 1;
        if (mvB === null) return -1;
        return mvB - mvA;
      }

      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // Empty state when filtering by availability and no players match
    if (
      (squadFilter === "available_loan" || squadFilter === "available_sale") &&
      filteredSquad.length === 0
    ) {
      return (
        <div className="text-center text-gray-500 py-8 bg-white rounded-lg border border-gray-200 shadow-sm">
          {squadFilter === "available_loan"
            ? "No players available for loan."
            : "No players available for sale."}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden border border-[#D6DBEA]">
        <div
          ref={scrollRef}
          {...handlers}
          className="overflow-x-auto"
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          }}
        >
          <table
            className="min-w-full divide-y divide-gray-200 table-sticky-first"
            style={{ minWidth: "1000px" }}
          >
            <thead className="bg-[#F7F7F7]">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Age
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Contract Expires
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Market Value
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Appearances
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Starts
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Minutes
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Goals
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Assists
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Yellow Cards
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                  Red Cards
                </th>
                {currentRole !== ROLES.AgencyAdmin &&
                  currentRole !== ROLES.Agent && (
                    <>
                      <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                        Availability
                      </th>
                    </>
                  )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedSquad.map((player) => (
                <tr
                  key={player.id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center gap-4 cursor-pointer flex-col sm:flex-row"
                      onClick={() => handlePlyaerNavigate(player.id)}
                    >
                      {/* Shirt number as a slim fixed-width sub-column left of
                          the player (>=sm). It lives INSIDE the first cell on
                          purpose: .table-sticky-first (index.css:217) pins the
                          FIRST td as the sticky identifying column on <=640px,
                          so a separate leading <td> would steal that anchor
                          from the player name. Below sm the number renders as
                          a prefix before the name instead (see <h4> below).
                          No number -> empty, no placeholder. */}
                      <span
                        className="hidden sm:block w-6 shrink-0 text-right text-[15px] text-[#6B7280] tabular-nums"
                        data-no-translate
                      >
                        {player.shirt_number ?? ""}
                      </span>
                      {player.image_url ? (
                        <div className="relative w-10 h-10 rounded-xl object-cover">
                          <img
                            src={player.image_url}
                            alt={player.name || "Player"}
                            className="w-full h-full object-cover rounded-xl"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 font-medium">
                          {player.name
                            ? player.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                            : "-"}
                        </div>
                      )}
                      <div>
                        <h4 className="font-bold text-[16px] text-[#222A35]">
                          {player.shirt_number != null && (
                            <span
                              className="sm:hidden font-medium text-[#6B7280] tabular-nums mr-1.5"
                              data-no-translate
                            >
                              {player.shirt_number}
                            </span>
                          )}
                          {player.name || "-"}
                        </h4>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.position || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.age || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {/* All citizenships, national team first. */}
                    <NationalityFlags
                      nationalities={player}
                      className="w-5 h-3.5 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.contract_expires_on || "-"}
                  </td>
                  <td
                    className="px-6 py-4 text-[16px] text-[#222A35] whitespace-nowrap tabular-nums"
                    data-no-translate
                  >
                    {/* Same field pair + formatter source as Discovery
                        (useDiscovery.js): market values come in EUR. */}
                    {formatCurrencyWithSymbol(
                      player.market_value,
                      player.market_currency || "EUR"
                    )}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.appearances || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.starts || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.minutes || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.goals || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.assists || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.yellow_card || "-"}
                  </td>
                  <td className="px-6 py-4 text-[16px] text-[#222A35]">
                    {player.season_stats?.red_cards || "-"}
                  </td>
                  {currentRole !== ROLES.AgencyAdmin &&
                    currentRole !== ROLES.Agent && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {player.is_available_for_loan && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Loan
                            </span>
                          )}
                          {player.is_available_for_sale && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Sale
                            </span>
                          )}
                          {!player.is_available_for_loan &&
                            !player.is_available_for_sale && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                Not Available
                              </span>
                            )}
                        </div>
                      </td>
                    )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Component to display Trophies
  const TrophiesSection = ({ trophies }) => {
    if (!trophies || trophies.length === 0) {
      return (
        <div className="text-center text-gray-500 py-8 bg-white rounded-lg border border-gray-200 shadow-sm">
          No trophies data available.
        </div>
      );
    }
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden border border-[#D6DBEA]">
        <table className="min-w-full divide-y divide-gray-200 table-sticky-first">
          <thead className="bg-[#F7F7F7]">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                Competition
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                Count
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#222A35] uppercase tracking-wider whitespace-nowrap">
                Seasons
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {trophies.map((trophy, index) => (
              <tr key={trophy.id || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-[16px] text-[#222A35]">
                  {trophy.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-[16px] text-[#222A35]">
                  {trophy.number}
                </td>
                <td className="px-6 py-4 text-[16px] text-[#222A35]">
                  {trophy.additionalData?.seasonIds?.join(", ") || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const mappingOfDivison = (divNumber) => {
    if (divNumber === 1) return "1st Tier";
    else if (divNumber === 2) return "2nd Tier";
    else if (divNumber === 3) return "3rd Tier";
    else return `${divNumber}th Tier`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center p-8 bg-white rounded-lg shadow border border-red-200 max-w-md w-full">
          <svg
            className="w-12 h-12 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <h2 className="text-xl font-semibold text-red-700 mb-2">
            Error Loading Club Profile
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Club data could not be loaded.</p>
      </div>
    );
  }

  const handlePlyaerNavigate = (playerId) => {
    navigate(`/search/player/${playerId}`);
  };

  const handleMessageClub = async (clubId) => {
    if (messaging) return;
    setMessaging(true);
    try {
      if (!clubId) {
        toast.error("Club information not available for messaging");
        setMessaging(false);
        return;
      }

      const response = await liveMarketService.createClubChat(clubId);
      if (response.success) {
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to start chat with club");
      }
    } catch (error) {
      console.error("Error creating club chat:", error);
      toast.error("Failed to start chat with club");
    } finally {
      setMessaging(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="sm:px-12 px-4 py-4 bg-white flex justify-between items-center border-b border-[#E5E6E8]">
        <div
          onClick={() => {
            navigate(-1);
          }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <div>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8327 6.99935H1.16602M1.16602 6.99935L6.99935 12.8327M1.16602 6.99935L6.99935 1.16602"
                stroke="#1E1E1E"
                stroke-width="1.875"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div className="text-[#222A35] text-xl font-bold">
            {club?.club?.name || "-"}
          </div>
        </div>
        {/* flex-wrap = declared 375px strategy (UI rule 1): with the new
            report button the action row WRAPS onto a second line instead of
            shrinking or overflowing. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {(() => {
            const worksForClub =
              currentUserProfile?.user_details?.works_for_club;
            const hideForStaffNoClub =
              currentRole === ROLES.Staff && worksForClub === false;
            if (hideForStaffNoClub) return null;
            return (
              <>
              <button
                type="button"
                onClick={() => setShowShareModal(true)}
                className="border border-[#E6E6E6] py-2 px-4 flex gap-2 items-center rounded-lg hover:bg-gray-50"
                aria-label="Share club profile"
              >
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.72667 9.00732L9.28 11.6607M9.27333 4.34065L4.72667 6.99398M13 3.33398C13 4.43855 12.1046 5.33398 11 5.33398C9.89543 5.33398 9 4.43855 9 3.33398C9 2.22941 9.89543 1.33398 11 1.33398C12.1046 1.33398 13 2.22941 13 3.33398ZM5 8.00065C5 9.10522 4.10457 10.0007 3 10.0007C1.89543 10.0007 1 9.10522 1 8.00065C1 6.89608 1.89543 6.00065 3 6.00065C4.10457 6.00065 5 6.89608 5 8.00065ZM13 12.6673C13 13.7719 12.1046 14.6673 11 14.6673C9.89543 14.6673 9 13.7719 9 12.6673C9 11.5627 9.89543 10.6673 11 10.6673C12.1046 10.6673 13 11.5627 13 12.6673Z"
                    stroke="#2C2C2C"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-[#1E1E1E]">Share</span>
              </button>
              {/* Report data issue (BUILD-ORDER-data-feedback): clubId IS the
                  ws_team_id (route /search/club/:clubId, clubService
                  .getClubPublicProfile) — exactly what the endpoint expects. */}
              <ReportDataIssueButton
                entityType="club"
                wyscoutId={Number(clubId) || null}
                entityName={club?.club?.name}
              />
              </>
            );
          })()}

          {currentRole !== ROLES.AgencyAdmin &&
            currentRole !== ROLES.Agent &&
            club?.club?.is_platform_user && (
              <button
                onClick={() => {
                  handleMessageClub(club?.club?.id);
                }}
                disabled={messaging}
                className="bg-[#2283FF] text-white py-2 px-4 rounded-lg"
              >
                {messaging ? "Starting Chat..." : "Message Club"}
              </button>
            )}
        </div>
      </div>
      {/* Hero Section - Updated with correct API fields */}

      <div className="sm:px-12 px-4 bg-white py-4">
        <div className="relative px-4 bg-gradient-to-r from-[#1A212F] to-[#374DF5] text-white overflow-hidden shadow-lg rounded-lg">
          <div className="flex justify-between mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row items-center gap-6 md:justify-between w-full">
              <div className="flex items-center gap-6 flex-col sm:flex-row">
                {club?.club?.image_url ? (
                  <img
                    src={club.club.image_url}
                    alt={`${club?.club?.name || "Club"} crest`}
                    className="w-24 h-24 object-contain bg-white/10 rounded-lg p-1 shadow-md"
                  />
                ) : (
                  <div className="w-24 h-24 bg-white/10 rounded-lg p-1 shadow-md flex items-center justify-center">
                    <span className="text-white/60 text-sm font-medium">
                      N/A
                    </span>
                  </div>
                )}
                <div className="text-center md:text-left w-fit">
                  <h1 className="text-3xl lg:text-4xl font-bold mb-2">
                    {club?.club?.name || "-"}
                  </h1>

                  <div className="w-full flex justify-between  items-center gap-2">
                    <div className="text-[#FFFFFFB2]">Official Name:</div>
                    <div className="text-[#FFFFFFB2]">
                      {club?.club?.official_name || "-"}
                    </div>
                  </div>

                  <div className="w-full flex justify-between  items-center gap-2">
                    <div className="text-[#FFFFFFB2]">Country:</div>
                    <div className="text-[#FFFFFFB2]">
                      {club?.club?.country || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="flex flex-wrap justify-center md:justify-end gap-3 mt-4 md:mt-0">
                {club?.competition && (
                  <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 text-center min-w-[130px]">
                    <div className="font-bold text-sm text-[#1A212F]">
                      {club.competition?.area} - {club.competition?.name}
                      {/* TheSports liefert keine Tier-Nummer: ohne Wert
                          keinen "nullth Tier"-Text rendern (Phase D). */}
                      {club.competition?.division_level != null && (
                        <> | {mappingOfDivison(club.competition.division_level)}</>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="absolute bottom-4 right-5">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  {/* Country flag not available in new API */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white ">
        <div className="sm:px-12 px-4">
          <div className="flex gap-6 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-1 px-3 rounded-lg font-medium relative whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? "text-[#0B0B0B] text-[16px] bg-[#F3F4F6]"
                    : "text-[#1E1E1ECC] text-[16px] hover:text-gray-800 border-b-2 border-transparent hover:border-gray-300"
                }`}
                aria-current={activeTab === tab.id ? "page" : undefined}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Squad Filter (Only show for Squad tab) */}
      {activeTab === "squad" && (
        <div className="bg-white">
          <div className="mx-auto sm:px-12 px-4 py-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              {/* Availability Filters */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setSquadFilter("all")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-150 ${
                    squadFilter === "all"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  All Players
                </button>
                {currentRole !== ROLES.AgencyAdmin &&
                  currentRole !== ROLES.Agent && (
                    <>
                      <button
                        onClick={() => setSquadFilter("available_loan")}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-150 ${
                          squadFilter === "available_loan"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Available for Loan
                      </button>
                      <button
                        onClick={() => setSquadFilter("available_sale")}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-150 ${
                          squadFilter === "available_sale"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Available for Sale
                      </button>
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* postitons tabs */}

      {/* position tabs */}
      {activeTab === "squad" && (
        <div className="sm:px-12 px-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="bg-[#F8F9FB] border border-[#E5E6E8]  p-1 rounded-lg">
              <div className="flex gap-6 overflow-x-auto scrollbar">
                {positionTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActivePosition(tab.id)}
                    className={`py-2 px-3 rounded-md font-medium relative whitespace-nowrap text-sm ${
                      activePosition === tab.id
                        ? "text-black text-[14px] bg-white"
                        : "text-[#1E1E1ECC] text-[14px] hover:text-gray-800 "
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            {club?.season && (
              <div className="text-[#222A35] font-medium text-sm">
                Season : {club.season.name}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className=" mx-auto sm:px-12 px-4 py-8">
        {activeTab === "squad" && (
          <SquadSection squadData={club?.squad || []} />
        )}
        {activeTab === "matches" && <ClubMatchesTab clubId={clubId} />}

      {activeTab === "trophies" && <TrophiesSection trophies={[]} />}
        {activeTab === "staff" && (
          <StaffSection
            staffData={staff}
            loading={staffLoading}
            error={staffError}
          />
        )}
      </div>

      <ShareActionSheet
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onShare={handleShare}
        title={`Share ${club?.club?.name || "Club"} Profile`}
        shareUrl={shareUrl}
        shareType="club_public_profile"
        shareData={{
          club: {
            id: club?.club?.id,
            name: club?.club?.name || "Club Name",
          },
        }}
      />
    </div>
  );
};

export default ClubProfile;
